import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing room rates and prices
 *
 * UPDATED: Now uses apiService instead of direct axios calls
 * - Removed redundant Authorization headers
 * - Added request cancellation
 * - Better error handling
 */
export const useRates = (API) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null); // room or room type ID

  /**
   * Fetch rates for a specific room or room type
   * Now includes request cancellation
   */
  const fetchRates = useCallback(async (targetId) => {
    if (!targetId) return;

    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Determine if this is a room type or individual room
      const queryParam = targetId.startsWith('type-')
        ? `room_type_id=${targetId.replace('type-', '')}`
        : `room_id=${targetId}`;

      const response = await apiService.get(`/admin/rates?${queryParam}`, {
        signal: controller.signal
      });

      setRates(response.data);
      setSelectedTarget(targetId);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError(err);
      toast.error('Failed to fetch rates');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  /**
   * Fetch synced rates from Beds24
   */
  const fetchSyncedRates = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiService.get('/admin/rates?source=beds24_sync');
      return { success: true, data: response.data };
    } catch (err) {
      toast.error('Failed to fetch synced rates from Beds24');
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create rate
   */
  const createRate = useCallback(async (rateData, targetId) => {
    try {
      const payload = {
        ...rateData,
        price: parseFloat(rateData.price)
      };

      // Determine if this is a room type or individual room
      if (targetId.startsWith('type-')) {
        payload.room_type_id = targetId.replace('type-', '');
      } else {
        payload.room_id = targetId;
      }

      await apiService.post('/admin/rates', payload);
      toast.success('Rate created successfully');
      await fetchRates(targetId);
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create rate');
      return { success: false, error: err };
    }
  }, [fetchRates]);

  /**
   * Update rate
   */
  const updateRate = useCallback(async (rateId, rateData) => {
    try {
      await apiService.put(`/admin/rates/${rateId}`, {
        ...rateData,
        price: rateData.price ? parseFloat(rateData.price) : undefined
      });
      toast.success('Rate updated successfully');
      if (selectedTarget) {
        await fetchRates(selectedTarget);
      }
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update rate');
      return { success: false, error: err };
    }
  }, [fetchRates, selectedTarget]);

  /**
   * Delete rate
   */
  const deleteRate = useCallback(async (rateId) => {
    try {
      await apiService.delete(`/admin/rates/${rateId}`);
      toast.success('Rate deleted successfully');
      if (selectedTarget) {
        await fetchRates(selectedTarget);
      }
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete rate');
      return { success: false, error: err };
    }
  }, [fetchRates, selectedTarget]);

  /**
   * Clear all rates
   * FIXED: No more redundant auth header!
   */
  const clearAllRates = useCallback(async () => {
    try {
      const response = await apiService.delete('/admin/rates/clear/all');
      toast.success(`✅ ${response.data.message}`);
      setRates([]);
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear rates');
      return { success: false, error: err };
    }
  }, []);

  /**
   * Sync prices from Beds24
   * FIXED: No more redundant auth header!
   */
  const syncPricesFromBeds24 = useCallback(async (dateRange) => {
    try {
      const response = await apiService.post('/admin/beds24/sync-prices', dateRange);

      const result = response.data;
      if (result.total_prices > 0) {
        toast.success(`✅ Synced ${result.total_prices} price(s) for ${result.rooms_synced} room(s) from Beds24!`);
        if (selectedTarget) {
          await fetchRates(selectedTarget);
        }
      } else {
        toast.info('No prices found in Beds24 for the selected date range.');
      }

      return { success: true, data: result };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      toast.error(`Sync failed: ${errorMsg}`);
      return { success: false, error: err };
    }
  }, [fetchRates, selectedTarget]);

  return {
    rates,
    loading,
    error,
    selectedTarget,
    fetchRates,
    fetchSyncedRates,
    createRate,
    updateRate,
    deleteRate,
    clearAllRates,
    syncPricesFromBeds24,
  };
};
